import api from './api.service';

/** Paliers vendus (saas/12) — quotas de colocations publiées. */
export type BillingTier = 'solo' | 'trio' | 'illimite';
export type BillingInterval = 'monthly' | 'yearly';

export interface Subscription {
  tier: BillingTier | null;
  interval: BillingInterval | null;
  status: string;
  /** Fin de la période payée : renouvellement, ou fin d'accès si résilié. */
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** Fin de l'essai gratuit — null dès que l'abonnement est payant. */
  trialEnd: string | null;
  /** Quota de colocations publiées du palier. null = illimité. */
  publishedQuota: number | null;
}

export const billingService = {
  /**
   * Crée une session Stripe Checkout (hébergée) et renvoie son URL.
   * L'essai de 14 jours démarre à l'enregistrement de la carte, côté Stripe.
   */
  async createCheckoutSession(tier: BillingTier, interval: BillingInterval = 'monthly'): Promise<string> {
    const res = await api.post('/billing/checkout-session', { tier, interval });
    return res.data.url;
  },

  /** Résiliation en fin de période courante (essai inclus : rien débité). */
  async cancelSubscription(): Promise<void> {
    await api.post('/billing/cancel');
  },

  /**
   * Annule une résiliation programmée, tant que l'échéance n'est pas passée.
   * Sans elle, un bailleur qui se ravise devait attendre l'expiration — donc la
   * dépublication de son site — puis reprendre un checkout complet.
   */
  async resumeSubscription(): Promise<void> {
    await api.post('/billing/resume');
  },

  /**
   * Ouvre une session du portail client Stripe et renvoie son URL : changer
   * d'offre (Stripe calcule le prorata), de carte, télécharger ses factures.
   * 409 si le compte n'a pas d'abonnement Stripe.
   */
  async createPortalSession(): Promise<string> {
    const res = await api.post('/billing/portal-session');
    return res.data.url;
  },

  /** État de l'abonnement tel que l'écran « Mon compte » l'affiche. */
  async getSubscription(): Promise<Subscription> {
    const res = await api.get('/billing/subscription');
    return res.data;
  },
};
